class MasonryGallery {
  constructor(sectionId, options = {}) {
    this.sectionId = sectionId;
    this.options = {
      enableProjectOverlay: true,
      ...options
    };

    this.gallery = document.querySelector(`.media-grid-${sectionId}`);
    if (!this.gallery) return;

    this.gridItems = this.gallery.querySelectorAll('.media-grid-item');
    this.projectData = null;
    this.overlay = null;

    this.init();
    this.initClientFilter();
  }

  init() {
    this.initVideoEffects();
    if (this.options.enableProjectOverlay) {
      this.loadProjectData();
      this.initProjectOverlay();
    }
  }

  initClientFilter() {
    const sidebar = document.querySelector('.media-grid-sidebar');
    if (sidebar) {
      this.clientFilter = new ClientFilter(this.sectionId, this);
    }
  }

  loadProjectData() {
    const dataEl = document.getElementById(`project-data-${this.sectionId}`);
    if (!dataEl) return;
    try {
      this.projectData = JSON.parse(dataEl.textContent);
    } catch (e) {
      console.error('Failed to parse project data:', e);
    }
  }

  initProjectOverlay() {
    this.overlay = document.getElementById(`project-overlay-${this.sectionId}`);
    if (!this.overlay) return;

    this.overlayBackdrop = this.overlay.querySelector('.project-overlay__backdrop');
    this.overlayClose = this.overlay.querySelector('.project-overlay__close');
    this.overlayBody = this.overlay.querySelector('.project-overlay__body');
    this.overlayNumber = this.overlay.querySelector('.project-overlay__number');
    this.overlayClient = this.overlay.querySelector('.project-overlay__client');
    this.overlayService = this.overlay.querySelector('.project-overlay__service');
    this.overlayScrollContainer = this.overlay.querySelector('.project-overlay__scroll-container');

    this.gallery.querySelectorAll('.media-project-trigger').forEach(trigger => {
      trigger.addEventListener('click', (e) => this.openProjectOverlay(e));
      trigger.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          this.openProjectOverlay(e);
        }
      });
    });

    if (this.overlayClose) {
      this.overlayClose.addEventListener('click', () => this.closeProjectOverlay());
    }
    // Close when clicking anywhere outside the content box (backdrop, scroll padding, etc.)
    this.overlay.addEventListener('click', (e) => {
      if (!e.target.closest('.project-overlay__content') && !e.target.closest('.project-overlay__close')) {
        this.closeProjectOverlay();
      }
    });

    document.addEventListener('keydown', (e) => {
      if (!this.overlay.classList.contains('active')) return;
      if (e.key === 'Escape') {
        this.closeProjectOverlay();
      }
    });
  }

  openProjectOverlay(e) {
    const trigger = e.currentTarget;
    const gridItem = trigger.closest('.media-grid-item');
    if (!gridItem || !this.projectData) return;

    const clientHandle = gridItem.dataset.client;
    const project = this.projectData[clientHandle];
    if (!project) return;

    this.overlayNumber.textContent = project.projectNumber;
    this.overlayClient.textContent = project.clientName;
    this.overlayService.textContent = project.serviceType || '';

    this.overlayBody.innerHTML = this.buildProjectHTML(project);

    this.overlay.classList.add('active');
    document.body.classList.add('overlay-open');

    if (this.overlayScrollContainer) {
      this.overlayScrollContainer.scrollTop = 0;
    }

    this.overlayBody.querySelectorAll('video').forEach(v => {
      v.play().catch(() => {});
    });

    this.trapFocus();
  }

  buildProjectHTML(project) {
    const items = project.media;
    if (!items || items.length === 0) return '';

    let html = '';

    // Hero: first item, full width
    html += '<div class="project-hero">';
    html += this.renderMediaItem(items[0], true);
    html += '</div>';

    // Description (supports richtext HTML from Shopify)
    if (project.description) {
      html += '<div class="project-description">';
      html += project.description;
      html += '</div>';
    }

    // Remaining media in 2-column grid
    if (items.length > 1) {
      const remaining = items.slice(1);
      const remainingCount = remaining.length;

      html += '<div class="project-media-grid">';
      remaining.forEach((item, i) => {
        const isVideo = item.type === 'video';
        const isLastOdd = (i === remainingCount - 1) && (remainingCount % 2 === 1);
        const fullWidth = isVideo || isLastOdd;

        html += '<div class="project-media-item' + (fullWidth ? ' project-media--full' : '') + '">';
        html += this.renderMediaItem(item, false);
        html += '</div>';
      });
      html += '</div>';
    }

    return html;
  }

  renderMediaItem(item, isHero) {
    if (item.type === 'video') {
      return '<video autoplay muted loop playsinline controls' +
        (item.posterUrl ? ' poster="' + this.escapeAttr(item.posterUrl) + '"' : '') +
        '><source src="' + this.escapeAttr(item.videoUrl) + '"></video>';
    }
    return '<img src="' + this.escapeAttr(item.imageUrl) + '"' +
      (item.imageSrcset ? ' srcset="' + this.escapeAttr(item.imageSrcset) + '"' : '') +
      ' sizes="(max-width: 749px) 100vw, (max-width: 1100px) 50vw, 550px"' +
      ' alt="' + this.escapeAttr(item.alt || '') + '"' +
      ' loading="' + (isHero ? 'eager' : 'lazy') + '">';
  }

  escapeHTML(str) {
    const div = document.createElement('div');
    div.textContent = str;
    return div.innerHTML;
  }

  escapeAttr(str) {
    return String(str)
      .replace(/&/g, '&amp;')
      .replace(/"/g, '&quot;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');
  }

  closeProjectOverlay() {
    if (!this.overlay) return;

    this.overlay.querySelectorAll('video').forEach(v => v.pause());

    this.overlay.classList.remove('active');
    document.body.classList.remove('overlay-open');

    if (this.overlayBody) {
      this.overlayBody.innerHTML = '';
    }

    this.releaseFocus();
  }

  initVideoEffects() {
    const videoObserver = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          const video = entry.target;
          if (video.dataset.src && !video.src) {
            video.src = video.dataset.src;
            video.load();
          }
        }
      });
    }, { threshold: 0.1 });

    this.gallery.querySelectorAll('.media-video').forEach(video => {
      videoObserver.observe(video);

      const wrapper = video.closest('.media-item-inner');
      if (wrapper) {
        wrapper.addEventListener('mouseenter', () => {
          video.play().catch(() => {});
        });
        wrapper.addEventListener('mouseleave', () => {
          video.pause();
        });
      }
    });
  }

  trapFocus() {
    this.previouslyFocused = document.activeElement;
    const focusableElements = this.overlay.querySelectorAll(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );
    this.firstFocusable = focusableElements[0];
    this.lastFocusable = focusableElements[focusableElements.length - 1];
    this._boundHandleTabKey = this.handleTabKey.bind(this);
    this.overlay.addEventListener('keydown', this._boundHandleTabKey);
    if (this.overlayClose) this.overlayClose.focus();
  }

  releaseFocus() {
    if (this._boundHandleTabKey) {
      this.overlay.removeEventListener('keydown', this._boundHandleTabKey);
    }
    if (this.previouslyFocused && this.previouslyFocused.focus) {
      this.previouslyFocused.focus();
    }
  }

  handleTabKey(e) {
    if (e.key !== 'Tab') return;
    if (e.shiftKey) {
      if (document.activeElement === this.firstFocusable) {
        e.preventDefault();
        this.lastFocusable.focus();
      }
    } else {
      if (document.activeElement === this.lastFocusable) {
        e.preventDefault();
        this.firstFocusable.focus();
      }
    }
  }
}

// Client Filter Class
class ClientFilter {
  constructor(sectionId, galleryInstance) {
    this.sectionId = sectionId;
    this.gallery = galleryInstance;
    this.filterButtons = document.querySelectorAll(`.media-grid-section .filter-btn`);
    this.gridItems = document.querySelectorAll(`.media-grid-${sectionId} .media-grid-item`);
    this.currentFilter = 'all';

    if (this.filterButtons.length > 0) {
      this.init();
    }
  }

  init() {
    this.filterButtons.forEach((button) => {
      button.addEventListener('click', (e) => {
        e.preventDefault();
        const filter = button.dataset.filter || 'all';
        this.applyFilter(filter);
      });
    });
  }

  applyFilter(filter) {
    if (filter === this.currentFilter) return;
    if (this._isAnimating) return;

    this.currentFilter = filter;

    this.filterButtons.forEach(btn => {
      btn.classList.toggle('active', btn.dataset.filter === filter);
    });

    if (filter === 'all') {
      this.animateToAll();
    } else {
      this.animateToFiltered(filter);
    }
  }

  animateToFiltered(filter) {
    this._isAnimating = true;
    const FADE_DURATION = 300;

    const toHide = [];
    const toShow = [];

    this.gridItems.forEach(item => {
      const clientName = item.dataset.client || '';
      const matches = this.normalizeString(clientName) === this.normalizeString(filter);
      const isCurrentlyHidden = item.classList.contains('filter-hidden');

      if (matches && isCurrentlyHidden) {
        toShow.push(item);
      } else if (!matches && !isCurrentlyHidden) {
        toHide.push(item);
      }
    });

    // Phase 1: Fade out items that need to hide
    toHide.forEach(item => {
      item.classList.add('filter-fading-out');
    });

    setTimeout(() => {
      // Phase 2: Remove faded items from flow
      toHide.forEach(item => {
        item.classList.remove('filter-fading-out');
        item.classList.add('filter-hidden');
        item.style.display = 'none';
      });

      // Bring in items that need to appear (invisible initially)
      toShow.forEach(item => {
        item.classList.remove('filter-hidden');
        item.style.display = '';
        item.classList.add('filter-fading-in');
      });

      this.updateStaggerOffsets();

      // Phase 3: Fade in new items
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          toShow.forEach(item => {
            item.classList.remove('filter-fading-in');
            item.classList.add('filter-visible');
          });

          setTimeout(() => {
            toShow.forEach(item => {
              item.classList.remove('filter-visible');
            });
            this._isAnimating = false;
          }, FADE_DURATION);
        });
      });
    }, FADE_DURATION);
  }

  animateToAll() {
    this._isAnimating = true;
    const FADE_DURATION = 300;

    const hiddenItems = Array.from(this.gridItems).filter(item =>
      item.classList.contains('filter-hidden')
    );

    if (hiddenItems.length === 0) {
      this._isAnimating = false;
      this.updateStaggerOffsets();
      return;
    }

    // Put hidden items back in flow but invisible
    hiddenItems.forEach(item => {
      item.classList.remove('filter-hidden');
      item.style.display = '';
      item.classList.add('filter-fading-in');
    });

    this.updateStaggerOffsets();

    // Fade them in
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        hiddenItems.forEach(item => {
          item.classList.remove('filter-fading-in');
          item.classList.add('filter-visible');
        });

        setTimeout(() => {
          hiddenItems.forEach(item => {
            item.classList.remove('filter-visible');
            item.style.marginTop = '';
          });
          this._isAnimating = false;
        }, FADE_DURATION);
      });
    });
  }

  updateStaggerOffsets() {
    const visibleItems = this.getVisibleItems();
    const w = window.innerWidth;
    const stagger = w <= 749 ? '' : w <= 989 ? '20px' : '40px';
    visibleItems.forEach((item, i) => {
      const pos = i % 4;
      if (pos === 1 || pos === 2) {
        item.style.marginTop = stagger;
      } else {
        item.style.marginTop = '';
      }
    });
  }

  resetToOriginalState() {
    this.gridItems.forEach(item => {
      item.classList.remove('filter-hidden', 'filter-fading-out', 'filter-fading-in', 'filter-visible');
      item.style.display = '';
      item.style.marginTop = '';
    });
  }

  filterItemsByClient(filter) {
    this.gridItems.forEach(item => {
      const clientName = item.dataset.client || '';
      const shouldShow = this.normalizeString(clientName) === this.normalizeString(filter);

      if (shouldShow) {
        item.classList.remove('filter-hidden');
        item.style.display = '';
      } else {
        item.classList.add('filter-hidden');
        item.style.display = 'none';
      }
    });
  }

  normalizeString(str) {
    return str.toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
  }

  getVisibleItems() {
    return Array.from(this.gridItems).filter(item => {
      return !item.classList.contains('filter-hidden') &&
             item.style.display !== 'none' &&
             !item.hasAttribute('hidden');
    });
  }
}

// Auto-initialize galleries
const initializeGalleries = () => {
  document.querySelectorAll('[data-media-grid-id]').forEach(gallery => {
    const sectionId = gallery.dataset.mediaGridId;
    if (!gallery.dataset.initialized) {
      new MasonryGallery(sectionId, {
        enableProjectOverlay: gallery.dataset.enableProjectOverlay !== 'false'
      });
      gallery.dataset.initialized = 'true';
    }
  });
};

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initializeGalleries);
} else {
  initializeGalleries();
}

window.addEventListener('load', () => {
  setTimeout(initializeGalleries, 100);
});

window.MasonryGallery = MasonryGallery;